import clsx from 'clsx';
import withPortal from 'components/react-helpers/WithPortal';
import { ReactNode, useEffect, useRef } from 'react';

import Cross40 from '../svg/40x40/Cross40.svg';
import styles from './SlidingModal.module.css';

export interface Props {
  isOpen: boolean;
  onClose: () => void;
  background?: 'white' | 'darkBlue';
  /** The amount that the modal slides in. See the CSS for its exact behaviour, the effect may not be different on mobile vs desktop */
  slideAmount?: '50percent' | 'static';
  /** A custom element to render where the close button goes. The onClose event will be provided */
  customCloseButton?: (onClose: () => void) => ReactNode;
}

interface PropsWithChildren extends Props {
  children: ReactNode;
}

/** A modal that slides in from the bottom (on mobile) or the side (on desktop) */
const SlidingModal = (props: PropsWithChildren) => {
  const scrollableContent = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (props.isOpen && scrollableContent.current) {
      scrollableContent.current?.scrollTo({ top: 0 });
    }
  }, [props.isOpen]);

  return (
    <>
      <div
        className={clsx(styles.backdrop, {
          [styles.visible]: props.isOpen,
        })}
        onClick={props.onClose}
      />
      <div
        className={clsx('daymadeTypographyReset', styles.container, {
          [styles.visible]: props.isOpen,
          [styles.darkBlue]: props.background === 'darkBlue',
          [styles.fixedSlideAmount]: props.slideAmount === 'static',
        })}
      >
        <div className={styles.modalContent}>
          <div className={styles.header}>
            {props.customCloseButton ? (
              props.customCloseButton(props.onClose)
            ) : (
              <button
                className={styles.closeButton}
                onClick={props.onClose}
                aria-label="Close modal"
              >
                <Cross40 />
              </button>
            )}
          </div>
          <div ref={scrollableContent} className={styles.scrollableContent}>
            {props.children}
          </div>
        </div>
      </div>
    </>
  );
};

export default withPortal(SlidingModal);
